import React, { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
  writeBatch,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import bg from "./mcd.png";

export default function RequestsAdminPage() {
  // =========================
  // AUTH ANÓNIMO (para permisos)
  // =========================
  const [authReady, setAuthReady] = useState(false);

useEffect(() => {
  const auth = getAuth();

  const unsub = onAuthStateChanged(auth, async (user) => {
    if (user) {
      setAuthReady(true);
      return;
    }
    try {
      await signInAnonymously(auth);
      setAuthReady(true);
    } catch (e) {
      console.error("Auth error:", e);
      setAuthReady(false);
    }
  });

  return () => unsub();
}, []);


  // =========================
  // DATA
  // =========================
  const [requests, setRequests] = useState([]);
  const [items, setItems] = useState([]);

  // cargar inventario (items)
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "items"), (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(arr);
    });
    return () => unsub();
  }, []);

  // cargar peticiones (requests)
  useEffect(() => {
  const unsub = onSnapshot(collection(db, "requests"), (snap) => {
    const arr = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((r) => r.status !== "deleted"); // <-- ocultar borradas
    setRequests(arr);
  });
  return () => unsub();
}, []);

  // =========================
  // Helpers
  // =========================
  const keyOf = (x) =>
    [
      x.tipo || "",
      x.sexo || "",
      x.rol || "",
      x.talla || "",
      x.entrenador ? "Entrenador" : "Normal",
      x.premama ? "Premama" : "Normal",
    ].join("|");

  const itemsMap = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      m.set(keyOf(it), it);
    }
    return m;
  }, [items]);

  const rows = useMemo(() => {
    return requests.map((r) => {
      const match = itemsMap.get(keyOf(r));
      const disponible = match ? Number(match.cantidad || 0) : 0;
      const pedido = Number(r.cantidad || 0);
      return {
        ...r,
        disponible,
        pedido,
        itemId: match?.id || null,
      };
    });
  }, [requests, itemsMap]);

    // ==============================
  // Helper: disponibilidad para una petición
  // (NO cambia funcionalidad, solo define lo que ya se usa)
  // ==============================
  const availableForRequest = (req) => {
    const pedido = Number(req.cantidad) || 0;

    const match = items.find((it) => {
      const sameBase =
        it.tipo === req.tipo &&
        it.sexo === req.sexo &&
        it.rol === req.rol &&
        it.talla === req.talla;

      if (!sameBase) return false;

      // Diferenciar camisetas Crew de Entrenador
      if (req.tipo === "Camiseta" && req.rol === "Crew") {
        return Boolean(it.entrenador) === Boolean(req.entrenador);
      }

      // Diferenciar pantalón premamá (solo mujer)
      if (req.tipo === "Pantalón" && req.sexo === "Mujer") {
        return Boolean(it.premama) === Boolean(req.premama);
      }

      return true;
    });

    if (!match) {
      return {
        ok: false,
        disponible: 0,
        itemId: null,
        pedido,
      };
    }

    return {
      ok: true,
      disponible: Number(match.cantidad) || 0,
      itemId: match.id,
      pedido,
    };
  };

  // =========================
  // Acciones
 // =========================
async function acceptRequest(r) {
  const av = availableForRequest(r);

  if (!av.itemId) {
    alert("No existe este uniforme en inventario.");
    return;
  }
  if (av.disponible < av.pedido) {
    alert("No hay stock suficiente para aceptar esta petición.");
    return;
  }

  try {
    // 🔐 GARANTIZAR AUTH ANÓNIMO AQUÍ MISMO
    const auth = getAuth();
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
    console.log("AUTH UID:", auth.currentUser?.uid);

    const batch = writeBatch(db);

    const itemRef = doc(db, "items", av.itemId);
    const reqRef  = doc(db, "requests", r.id);

    // 1) Descontar stock
    batch.update(itemRef, {
      cantidad: av.disponible - av.pedido,
    });

    // 2) Guardar histórico de aceptadas
    const acceptedRef = doc(collection(db, "acceptedRequests"));
    batch.set(acceptedRef, {
      ...r,
      acceptedAt: serverTimestamp(),
      acceptedBy: auth.currentUser?.uid || "admin-anon",
    });

    // 3) Borrar petición pendiente
    batch.delete(reqRef);

    await batch.commit();
    alert("Petición aceptada y stock actualizado.");
  } catch (e) {
    console.error("acceptRequest error:", e);
    alert("Error al aceptar la petición.");
  }
}

 async function deleteRequestOnly(r) {
  if (!r?.id) return;

  const ok = window.confirm("¿Quieres borrar esta petición? (No afecta inventario)");
  if (!ok) return;
console.log("PROJECT:", db.app?.options?.projectId);

  try {
    await deleteDoc(doc(db, "requests", r.id));
    alert("Petición borrada.");
  } catch (e) {
    console.error(e);
    alert("Error al borrar la petición.");
  }
}

  // =========================
  // UI
  // =========================
  return (
    <div
      className="min-h-screen bg-cover bg-center"
      style={{
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
      }}
    >
      <div className="min-h-screen bg-white/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-emerald-900 mb-4">
            Peticiones de uniformes
          </h1>

          {!authReady && (
            <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-900">
              Conectando con Firebase...
            </div>
          )}

          <div className="bg-white rounded-2xl shadow p-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-amber-50">
                <tr className="text-left">
                  <th className="p-2">Restaurante</th>
                  <th className="p-2">Empleado</th>
                  <th className="p-2">Tipo</th>
                  <th className="p-2">Sexo</th>
                  <th className="p-2">Rol</th>
                  <th className="p-2">Talla</th>
                  <th className="p-2 text-right">Cantidad</th>
                  <th className="p-2 text-right">Disponible</th>
                  <th className="p-2 text-right">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="p-6 text-center text-slate-500">
                      No hay peticiones pendientes.
                    </td>
                  </tr>
                )}

                {rows.map((r) => {
                  const faltaStock = r.disponible < r.pedido;
                  return (
                    <tr
                      key={r.id}
                      className={`border-t ${
                        faltaStock ? "bg-red-50" : "bg-white"
                      }`}
                    >
                      <td className="p-2">
                        {r.restaurante || "-"}
                      </td>
                      <td className="p-2">
                        {r.empleado || "-"}
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col">
                          <span>{r.tipo}</span>
                          {r.tipo === "Camiseta" && r.rol === "Crew" && r.entrenador && (
                            <span className="text-xs font-semibold text-blue-700">
                              Entrenador
                            </span>
                          )}
                          
                          {r.tipo === "Pantalón" && r.sexo === "Mujer" && r.premama && (
                            <span className="text-xs font-semibold text-pink-700">
                              Premamá
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-2">{r.sexo}</td>
                      <td className="p-2">{r.rol}</td>
                      <td className="p-2">{r.talla}</td>

                      <td className="p-2 text-right font-semibold">
                        {r.pedido}
                      </td>

                      <td
                        className={`p-2 text-right font-semibold ${
                          faltaStock ? "text-red-700" : "text-emerald-800"
                        }`}
                      >
                        {r.disponible}
                      </td>

                      <td className="p-2 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => acceptRequest(r)}
                            disabled={!authReady || faltaStock}
                            className={`px-3 py-1 rounded-lg border ${
                              !authReady || faltaStock
                                ? "opacity-50 cursor-not-allowed bg-slate-100"
                                : "bg-emerald-600 text-white hover:bg-emerald-700"
                            }`}
                          >
                            Aceptar
                          </button>

                          <button
                            onClick={() => deleteRequestOnly(r)}
                            disabled={!authReady}
                            className={`px-3 py-1 rounded-lg border ${
                              !authReady
                                ? "opacity-50 cursor-not-allowed bg-slate-100"
                                : "bg-white text-red-700 hover:bg-red-50 border-red-200"
                            }`}
                          >
                            Borrar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {rows.length > 0 && (
              <p className="mt-3 text-xs text-slate-500">
                Las filas en rojo indican falta de stock para esa petición.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
